import { useEffect, useState } from "react";
import { Bell, BellOff, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  pushSupported,
  permissionState,
  currentSubscription,
  enablePush,
  isIos,
  isStandalone,
} from "../lib/push";
import { Link } from "react-router-dom";

/**
 * Compact push-notification status pill for the admin top bar.
 * - Not subscribed → prominent gold "Aktiver varsler" button
 * - Subscribed → subtle green "Varsler på" badge
 * - Permission denied → red badge that links to /profile with instructions
 * - iOS Safari (not PWA) → yellow "Installer app for varsler" link
 */
export default function PushStatusButton() {
  const [subscribed, setSubscribed] = useState(null); // null = still loading
  const [perm, setPerm] = useState("default");
  const [busy, setBusy] = useState(false);
  const supported = pushSupported();
  const iosBrowser = isIos() && !isStandalone();

  const refresh = async () => {
    setPerm(permissionState());
    const sub = await currentSubscription();
    setSubscribed(!!sub);
  };

  useEffect(() => {
    refresh();
    // Re-check every 30 s in case the user changes permission from settings.
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, []);

  const doEnable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await enablePush();
      toast.success("Push-varsler aktivert på denne enheten");
      await refresh();
    } catch (e) {
      toast.error(e.message || "Kunne ikke aktivere push");
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  if (!supported || subscribed === null) {
    // Loading or unsupported – render nothing so we don't block the top bar.
    return null;
  }

  // iOS in browser (must add to home screen first) – link to /profile so
  // the user sees the full instructions.
  if (iosBrowser) {
    return (
      <Link
        to="/profile"
        data-testid="push-status-ios"
        className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-200 hover:text-white px-2 sm:px-3 py-1.5 text-xs font-semibold uppercase tracking-widest transition"
        title="Aktiver varsler ved å legge til på hjemskjermen"
      >
        <Bell className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Aktiver på hjemskjerm</span>
      </Link>
    );
  }

  // Permission denied – must reset from browser settings.
  if (perm === "denied") {
    return (
      <Link
        to="/profile"
        data-testid="push-status-denied"
        className="inline-flex items-center gap-1.5 rounded-full bg-red-500/20 border border-red-400/40 text-red-200 hover:text-white px-2 sm:px-3 py-1.5 text-xs font-semibold uppercase tracking-widest transition"
        title="Varsler blokkert – trykk for hjelp"
      >
        <BellOff className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Varsler blokkert</span>
      </Link>
    );
  }

  if (!subscribed) {
    return (
      <button
        type="button"
        onClick={doEnable}
        disabled={busy}
        data-testid="push-status-enable"
        title="Aktiver push-varsler"
        className="inline-flex items-center gap-1.5 rounded-full bg-[#BD984D] hover:bg-[#A57F39] text-[#0C1824] px-2 sm:px-3 py-1.5 text-xs font-bold uppercase tracking-widest shadow-lg transition disabled:opacity-60 animate-pulse-subtle"
      >
        {busy ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Bell className="w-3.5 h-3.5" />
        )}
        <span className="hidden sm:inline">Aktiver varsler</span>
      </button>
    );
  }

  return (
    <Link
      to="/profile"
      data-testid="push-status-on"
      className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/20 border border-emerald-400/30 text-emerald-200 hover:text-emerald-100 px-2 sm:px-3 py-1.5 text-xs font-semibold uppercase tracking-widest transition"
      title="Push-varsler er aktivert på denne enheten"
    >
      <CheckCircle2 className="w-3.5 h-3.5" />
      <span className="hidden sm:inline">Varsler på</span>
    </Link>
  );
}
